import { Component, inject, OnDestroy } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { CultureService } from '../../../shared/services/culture/culture.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { QuillModule } from 'ngx-quill';
import { QuillConfigService } from '../../../shared/services/quill-config.service';
import { StorageService } from '../../../shared/services/storage/storage.service';
import { UserProfile } from '../../../shared/interfaces/role.interface';
import { RolesService } from '../../../shared/services/roles/roles.service';

@Component({
  selector: 'app-mission-form',
  imports: [
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    FormsModule,
    CommonModule,
    RouterModule,
    LoaderComponent,
    RouterLink,
    QuillModule,
  ],
  templateUrl: './mission-form.component.html',
  styleUrls: [
    './mission-form.component.css',
    '../../../shared/styles/layout.css',
  ],
})
export class MissionFormComponent implements OnDestroy {
  // Mode management
  isEditMode: boolean = false;
  missionId: string | null = null;

  // Common properties
  modules: any;
  form: FormGroup;
  myroles: any = {};

  private _snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private loaderService: LoaderService,
    private cultureService: CultureService,
    private quillConfigService: QuillConfigService,
    private storageService: StorageService,
    private rolesService: RolesService
  ) {
    this.modules = this.quillConfigService.getDefaultModules();
    this.form = this.fb.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      is_vision: [false],
    });
  }

  get isLoading() {
    return this.loaderService.getLoader();
  }

  ngOnInit() {
    // Check if we're in edit mode based on route params
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (params['id']) {
        this.isEditMode = true;
        this.missionId = params['id'];
        this.loadMissionData();
      } else {
        this.isEditMode = false;
        this.missionId = null;
      }
    });

    this.loadRoles();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadMissionData(): void {
    if (!this.missionId) return;

    this.loaderService.showLoader();
    this.cultureService
      .getOneMission(this.missionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (mission) => {
          this.loaderService.hideLoader();
          this.form.patchValue({
            title: mission.title,
            description: mission.description,
            is_vision: mission.is_vision,
          });
        },
        error: (error) => {
          console.error('Error loading mission:', error);
          this.loaderService.hideLoader();
          this.openSnackBar(
            'Error al cargar la misión. Por favor intente de nuevo.'
          );
        },
      });
  }

  openSnackBar(message: string) {
    const action = 'Cerrar';
    this._snackBar.open(message, action, {
      duration: 5000,
    });
  }

  loadRoles() {
    const permissions = this.rolesService.getPermissionsForFunctionality(
      'mission_vision',
      this.storageService
    );

    if (permissions) {
      this.myroles = permissions;

      const permissionMessage = this.isEditMode
        ? 'No tienes permisos para editar una misión/visión'
        : 'No tienes permisos para añadir una misión/visión';

      this.rolesService.checkWritePermission(
        'mission_vision',
        this.storageService,
        this._snackBar,
        this.router,
        '/home/culture/mission',
        permissionMessage
      );
    } else {
      // console.log('No user roles found in storage');
    }
  }

  saveChanges() {
    if (this.form.valid) {
      this.loaderService.showLoader();

      if (this.isEditMode) {
        this.updateMission();
      } else {
        this.saveMission();
      }
    } else {
      // Mark all fields as touched to trigger validation messages
      this.form.markAllAsTouched();
      this.openSnackBar('Por favor, complete todos los campos requeridos.');
    }
  }

  private saveMission() {
    const newMission = this.form.value;

    this.cultureService
      .postMission(newMission)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loaderService.hideLoader();
          this.openSnackBar('Misión creada con éxito');
          this.router.navigate(['/home/culture/mission']);
        },
        error: (error) => {
          console.error('Error creating mission:', error);
          this.loaderService.hideLoader();
          this.openSnackBar(
            'Error al crear la misión. Por favor intente de nuevo.'
          );
        },
      });
  }

  private updateMission() {
    if (!this.missionId) return;

    const updatedMission = this.form.value;
    this.cultureService
      .updateMission(this.missionId, updatedMission)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loaderService.hideLoader();
          this.openSnackBar('Misión actualizada con éxito');
          this.router.navigate(['/home/culture/mission']);
        },
        error: (error) => {
          console.error('Error updating mission:', error);
          this.loaderService.hideLoader();
          this.openSnackBar(
            'Error al actualizar la misión. Por favor intente de nuevo.'
          );
        },
      });
  }
}
